import { useRef, useEffect, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Checkbox } from "@/components/shadcn/checkbox";
import MaterialIcon from "@/components/general/common/MaterialIcon";
import { cn } from "@/lib/utils";

interface MultiSelectProps {
    options: string[];
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder: string;
    icon: string;
    className?: string;
    buttonClassName?: string;
}

const MultiSelect = ({
    options,
    selected,
    onChange,
    placeholder,
    icon,
    className,
    buttonClassName,
}: MultiSelectProps) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    const toggle = (option: string) => {
        if (selected.includes(option)) {
            onChange(selected.filter((s) => s !== option));
        } else {
            onChange([...selected, option]);
        }
    };

    const clearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    const label =
        selected.length === 0 ? placeholder : selected.length === 1 ? selected[0] : `${selected.length} selected`;

    return (
        <div ref={ref} className={cn("relative", className)}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={cn(
                    "flex h-9 items-center gap-2 rounded-md bg-surface-container-highest px-3 py-2 text-sm cursor-pointer whitespace-nowrap",
                    buttonClassName
                )}
            >
                <MaterialIcon name={icon} className="text-primary text-lg" />
                <span className={selected.length === 0 ? "text-on-surface-variant" : "text-on-surface"}>{label}</span>
                {selected.length > 0 ? (
                    <X className="h-3.5 w-3.5 text-on-surface-variant hover:text-on-surface" onClick={clearAll} />
                ) : (
                    <ChevronDown
                        className={`h-3.5 w-3.5 text-on-surface-variant transition-transform ${open ? "rotate-180" : ""}`}
                    />
                )}
            </button>

            {open && options.length > 0 && (
                <div className="absolute top-full mt-1 right-0 z-50 min-w-[180px] rounded-md border border-outline-variant/20 bg-surface-container-lowest shadow-lg overflow-hidden">
                    <div className="max-h-60 overflow-y-auto p-1">
                        {options.map((option) => {
                            const checked = selected.includes(option);
                            return (
                                <label
                                    key={option}
                                    className="flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm cursor-pointer hover:bg-surface-container select-none"
                                >
                                    <Checkbox checked={checked} onCheckedChange={() => toggle(option)} />
                                    <span className="text-on-surface">{option}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelect;
